import { Autocomplete, type AutocompleteProps, Box, MenuItem, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useId } from 'react';

import { FormField, api, getQueryProps } from 'common';

import { FlowDefinitionStateTag } from '../../../../Definitions/common/StateTag.tsx';
import { useFlowDefinitionContext } from '../../../../context.ts';
import { useReactFlowViewContext } from '../../../context/context.tsx';

const FLOW_DEFINITION_ID_HELPER_TEXT = `Select the subflow that corresponds to an ICD code.`;

interface SubFlowAutocompleteProps extends Pick<
  AutocompleteProps<api.FlowDefinition.FlowDefinitionForList, false, false, false>,
  'onChange' | 'onBlur'
> {
  visibleError?: string;
  value: string | undefined;
  setValue: (option: string) => void;
  /**
   * We always restrict to flows within the same organization
   */
  flowDefinitionQueryParams?: Omit<
    api.FlowDefinition.FlowDefinitionFindAllOptions,
    'organizationId' | 'orderSpec'
  >;
  label?: string;
  helperText?: string;
}

export function SubFlowV0Autocomplete(props: SubFlowAutocompleteProps) {
  const { flowDefinitionQueryParams, visibleError, setValue, label, helperText, ...fieldProps } =
    props;
  const { isEditable } = useReactFlowViewContext();
  const { flowDefinition } = useFlowDefinitionContext();
  const { data: flowDefinitions, isLoading } = useQuery({
    ...getQueryProps(api.client.FlowDefinition, 'findAll', {
      // Restrict to flows within the same organization (across agent is ok)
      organizationId: flowDefinition.organizationId,
      ...flowDefinitionQueryParams,
      orderSpec: 'name|asc,version|desc',
    }),
  });

  const selectedFlowDefinition = flowDefinitions?.find(
    (flowDefinition) => flowDefinition.id === fieldProps.value
  );

  const fieldId = useId();
  return (
    <>
      <FormField
        id={fieldId}
        label={label || 'Subflow'}
        error={visibleError}
        helperText={helperText || FLOW_DEFINITION_ID_HELPER_TEXT}
      >
        <Autocomplete
          id={fieldId}
          {...fieldProps}
          multiple={false}
          options={flowDefinitions?.filter((fd) => fd.id !== flowDefinition.id) ?? []}
          loading={isLoading}
          disabled={!isEditable}
          value={selectedFlowDefinition ?? null}
          onChange={(_, newValue) => {
            if (newValue) {
              setValue(newValue.id);
            }
          }}
          renderInput={(params) => <TextField {...params} size="small" />}
          getOptionLabel={(option) => `${option.name} (v${option.version} ${option.state})`}
          renderOption={(listItemProps, option) => (
            <MenuItem {...listItemProps} key={option.id}>
              <Box display="flex" justifyContent="space-between" width="100%">
                <span>{`${option.name} v${option.version}`}</span>
                <span>
                  <FlowDefinitionStateTag state={option.state} />
                </span>
              </Box>
            </MenuItem>
          )}
        />
      </FormField>
    </>
  );
}
